import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Editable,
  EditablePreview,
  EditableInput,
  Text,
  Spinner,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  FaFileExcel,
  FaFilePdf,
  FaPlus,
  FaTrash,
  FaBoxes,
  FaUpload,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { DateTime } from "luxon";
import { useReactToPrint } from "react-to-print";
import Layout from "../../components/Layout";
import { crm } from "../../theme";
import { useCompany } from "../../contexts/CompanyContext";
import {
  getBoard,
  saveRow,
  deleteRow,
  updateField,
  uploadDocument,
  exportExcel,
} from "../../services/productionService";
import {
  ProductionOrderViewModel,
  productionOrderStatuses,
} from "../../models/Production";
import ProductsModal from "./components/ProductsModal";
import ProductionPrint from "./components/ProductionPrint";

const EditableCell = ({
  value,
  onSubmit,
}: {
  value?: string | null;
  onSubmit: (v: string) => void;
}) => (
  <Editable defaultValue={value ?? ""} placeholder="—" onSubmit={onSubmit}>
    <EditablePreview
      display="block"
      minW="80px"
      minH="24px"
      cursor="pointer"
      color={value ? undefined : crm.textMuted}
    />
    <EditableInput />
  </Editable>
);

// Data editável: só grava se o valor mudou (valor automático exibido não vira manual ao sair do campo).
const DateCell = ({
  value,
  onCommit,
  muted,
}: {
  value?: string | null;
  onCommit: (v: string | null) => void;
  muted?: boolean;
}) => {
  const initial = value ? DateTime.fromISO(value).toFormat("yyyy-LL-dd") : "";
  return (
    <input
      key={initial}
      type="date"
      style={{
        border: "none",
        background: "transparent",
        color: muted ? crm.textMuted : undefined,
        fontStyle: muted ? "italic" : undefined,
      }}
      defaultValue={initial}
      onBlur={(e) => {
        if (e.target.value !== initial)
          onCommit(e.target.value ? `${e.target.value}T00:00:00` : null);
      }}
    />
  );
};

const dateText = (value?: string | null) =>
  value ? DateTime.fromISO(value).toFormat("dd/LL/yyyy") : "—";

const readinessOf = (o: ProductionOrderViewModel) =>
  o.expectedReadinessDate ?? o.estimatedReadinessDate;

const dateFields = ["expectedReadinessDate", "readiness", "processOpeningDate", "productionStartDate"];

type SortField =
  | "processCvfRef"
  | "processOpeningDate"
  | "productionStartDate"
  | "readiness"
  | "containerQuantity"
  | "origin"
  | "brand"
  | "factory"
  | "category"
  | "expectedReadinessDate"
  | "status";

type SortDir = "asc" | "desc";

const SortableTh = ({
  label,
  field,
  sortField,
  sortDir,
  onSort,
}: {
  label: string;
  field: SortField;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) => {
  const active = sortField === field;
  return (
    <Th cursor="pointer" userSelect="none" onClick={() => onSort(field)}>
      <Flex align="center" gap="4px">
        {label}
        {active ? (
          sortDir === "asc" ? (
            <FaSortUp />
          ) : (
            <FaSortDown />
          )
        ) : (
          <FaSort opacity={0.3} />
        )}
      </Flex>
    </Th>
  );
};

const Backorder = () => {
  const { currentCompany, currentEmulation } = useCompany();
  const [orders, setOrders] = useState<ProductionOrderViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrderViewModel>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadRowIdRef = useRef<string>();
  const printRef = useRef<HTMLDivElement>(null);

  const get = async () => {
    setLoading(true);
    const response = await getBoard({
      type: currentCompany,
      companyId: currentEmulation,
      status: statusFilter || undefined,
    });
    setOrders(response ?? []);
    setLoading(false);
  };

  useEffect(() => {
    get();
  }, [currentCompany, currentEmulation, statusFilter]);

  const handleField = async (id: string, fieldName: string, value: any) => {
    const result = await updateField(id, fieldName, value);
    if (result && result.success === false) {
      toast({
        title: result.notifications?.[0] ?? "Erro ao atualizar",
        status: "error",
      });
    }
    get();
  };

  const handleAddManualRow = async () => {
    await saveRow({ status: "EM PRODUÇÃO", type: currentCompany });
    get();
  };

  const handleDelete = async (id: string) => {
    await deleteRow(id);
    get();
  };

  const handleUploadClick = (id: string) => {
    uploadRowIdRef.current = id;
    uploadInputRef.current?.click();
  };

  const handleUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const id = uploadRowIdRef.current;
    if (!file || !id) return;
    await uploadDocument(file, id);
    if (uploadInputRef.current) uploadInputRef.current.value = "";
    get();
  };

  const handleExportExcel = async () => {
    const base64 = await exportExcel({
      type: currentCompany,
      companyId: currentEmulation,
      status: statusFilter || undefined,
    });
    if (!base64) {
      toast({ title: "Erro ao gerar Excel", status: "error" });
      return;
    }
    const link = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
    const a = document.createElement("a");
    a.href = link;
    a.download = `Backorder ${DateTime.now().toFormat("dd-LL-yyyy")}.xlsx`;
    a.click();
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Backorder ${DateTime.now().toFormat("dd-LL-yyyy")}`,
  });

  const handleSort = (field: SortField) => {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
    }
  };

  const sortedOrders = useMemo(() => {
    if (!sortField) return orders;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...orders].sort((a, b) => {
      const pick = (o: ProductionOrderViewModel) =>
        sortField === "readiness" ? readinessOf(o) : o[sortField];
      const av = pick(a);
      const bv = pick(b);
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      if (dateFields.includes(sortField)) {
        return (
          (DateTime.fromISO(av as string).toMillis() -
            DateTime.fromISO(bv as string).toMillis()) *
          dir
        );
      }
      return (
        String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir
      );
    });
  }, [orders, sortField, sortDir]);

  return (
    <Box height="100%">
      <Layout>
        <Flex
          minHeight="100%"
          flexDirection="column"
          alignItems="center"
          backgroundColor="var(--main-bg-color)"
        >
          <Flex width="95%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box>
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                Backorder
              </Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">
                Pedidos em produção sincronizados dos processos + linhas manuais.
              </Text>
            </Box>
          </Flex>

          <Flex width="95%" justifyContent="space-between" mb="16px" gap="16px" flexWrap="wrap">
            <Select
              w="220px"
              backgroundColor="white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Todos os status</option>
              {productionOrderStatuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>

            <Flex gap="12px" flexWrap="wrap">
              <Button
                variant="outline"
                borderRadius="10px"
                borderColor={crm.border}
                color={crm.text}
                onClick={() => handlePrint()}
              >
                <Flex align="center" gap="8px">
                  <FaFilePdf /> PDF
                </Flex>
              </Button>
              <Button
                color="white"
                borderRadius="10px"
                backgroundColor={crm.success}
                _hover={{ backgroundColor: "#15803d" }}
                onClick={handleExportExcel}
              >
                <Flex align="center" gap="8px">
                  <FaFileExcel /> Excel
                </Flex>
              </Button>
              <Button
                color="white"
                borderRadius="10px"
                backgroundColor={crm.brand}
                _hover={{ backgroundColor: crm.brandDark }}
                onClick={handleAddManualRow}
              >
                <Flex align="center" gap="8px">
                  <FaPlus /> Linha manual
                </Flex>
              </Button>
            </Flex>
          </Flex>

          <Box
            w="95%"
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
            overflow="hidden"
            mb="48px"
          >
            {loading ? (
              <Flex justify="center" py="48px">
                <Spinner color={crm.brand} />
              </Flex>
            ) : (
              <Box
                maxH="calc(100vh - 260px)"
                overflow="auto"
                sx={{
                  // Cabeçalho e Nº Processo fixos, igual à tabela de processos
                  "thead th": {
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: crm.columnBg,
                    boxShadow: `0 1px 0 0 ${crm.border}`,
                  },
                  "thead th:first-of-type": { left: 0, zIndex: 3 },
                  "tbody td:first-of-type": {
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    backgroundColor: crm.surface,
                    boxShadow: `1px 0 0 0 ${crm.border}`,
                  },
                  table: { borderCollapse: "separate", borderSpacing: 0 },
                }}
              >
                <Table size="sm">
                  <Thead bg={crm.columnBg}>
                    <Tr>
                      <SortableTh label="Nº Processo" field="processCvfRef" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Abertura do processo" field="processOpeningDate" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Início da produção" field="productionStartDate" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Nº Containers" field="containerQuantity" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Origem" field="origin" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Marca" field="brand" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Fábrica" field="factory" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Categoria" field="category" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh label="Prontidão estimada" field="readiness" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <Th>Documento</Th>
                      <SortableTh label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <Th>Produtos</Th>
                      <Th></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedOrders.map((o) => (
                      <Tr key={o.id}>
                        <Td>
                          <EditableCell
                            key={`${o.id}-${o.processCvfRef ?? ""}`}
                            value={o.processCvfRef}
                            onSubmit={(v) => handleField(o.id!, "ProcessCvfRef", v)}
                          />
                        </Td>
                        <Td color={o.processOpeningDate ? undefined : crm.textMuted}>
                          {dateText(o.processOpeningDate)}
                        </Td>
                        <Td>
                          <Tooltip
                            label={
                              o.productionStartIsManual
                                ? "Informado à mão (apague para voltar ao automático)"
                                : "Automático: entrada do processo em MERCADORIA EM PRODUÇÃO"
                            }
                            openDelay={400}
                          >
                            <Box>
                              <DateCell
                                value={o.productionStartDate}
                                muted={!o.productionStartIsManual}
                                onCommit={(v) => handleField(o.id!, "ProductionStartDate", v)}
                              />
                            </Box>
                          </Tooltip>
                        </Td>
                        <Td>
                          <EditableCell
                            value={o.containerQuantity}
                            onSubmit={(v) => handleField(o.id!, "ContainerQuantity", v)}
                          />
                        </Td>
                        <Td>
                          <EditableCell
                            value={o.origin}
                            onSubmit={(v) => handleField(o.id!, "Origin", v)}
                          />
                        </Td>
                        <Td>
                          <EditableCell
                            value={o.brand}
                            onSubmit={(v) => handleField(o.id!, "Brand", v)}
                          />
                        </Td>
                        <Td>
                          <EditableCell
                            value={o.factory}
                            onSubmit={(v) => handleField(o.id!, "Factory", v)}
                          />
                        </Td>
                        <Td>
                          <EditableCell
                            value={o.category}
                            onSubmit={(v) => handleField(o.id!, "Category", v)}
                          />
                        </Td>
                        <Td>
                          <Tooltip
                            label={
                              o.expectedReadinessDate
                                ? "Informada à mão (apague para voltar à estimativa)"
                                : o.estimatedReadinessDate
                                  ? `Estimada: início da produção + ${o.supplierProductionDays} dias (${o.supplierName})`
                                  : o.supplierName
                                    ? `Fornecedor ${o.supplierName} sem tempo de produção cadastrado`
                                    : "Fábrica sem fornecedor em Comercial › Fornecedores"
                            }
                            openDelay={400}
                          >
                            <Box>
                              <DateCell
                                value={readinessOf(o)}
                                muted={!o.expectedReadinessDate}
                                onCommit={(v) => handleField(o.id!, "ExpectedReadinessDate", v)}
                              />
                            </Box>
                          </Tooltip>
                        </Td>
                        <Td>
                          {o.documentPath || o.processId ? (
                            o.processId ? (
                              <a href={`/process?id=${o.processId}`}>Ver processo</a>
                            ) : (
                              <a href="#" onClick={(e) => e.preventDefault()}>
                                Documento
                              </a>
                            )
                          ) : (
                            <IconButton
                              size="sm"
                              variant="ghost"
                              aria-label="Upload"
                              icon={<FaUpload />}
                              onClick={() => handleUploadClick(o.id!)}
                            />
                          )}
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            value={o.status ?? "EM PRODUÇÃO"}
                            onChange={(e) => handleField(o.id!, "Status", e.target.value)}
                          >
                            {productionOrderStatuses.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </Select>
                        </Td>
                        <Td>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            color={crm.brand}
                            aria-label="Produtos"
                            icon={<FaBoxes />}
                            onClick={() => {
                              setSelectedOrder(o);
                              onOpen();
                            }}
                          />
                        </Td>
                        <Td>
                          <IconButton
                            size="sm"
                            variant="ghost"
                            color="red"
                            aria-label="Excluir"
                            icon={<FaTrash />}
                            onClick={() => handleDelete(o.id!)}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {orders.length === 0 && (
                  <Flex justify="center" py="32px">
                    <Text color={crm.textMuted}>Nenhuma linha de produção</Text>
                  </Flex>
                )}
              </Box>
            )}
          </Box>
        </Flex>
      </Layout>

      <input
        type="file"
        ref={uploadInputRef}
        style={{ display: "none" }}
        onChange={handleUploadChange}
      />

      <div style={{ display: "none" }}>
        <ProductionPrint printRef={printRef} orders={orders} />
      </div>

      {selectedOrder && (
        <ProductsModal
          isOpen={isOpen}
          onClose={onClose}
          order={selectedOrder}
          onSaved={get}
        />
      )}
    </Box>
  );
};

export default Backorder;
